import { cloneDeep } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  RelationActiveHandle,
  useRelationActiveHandle,
} from './useRelationActiveHandle';

export interface BaseRelationEditor<B, T = WithId<B>> {
  items: T[];
  /** Inserisce un elemento alla posizione indicata */
  insert: (index: number, item: T) => T;
  /** Aggiunge un elemento alla fine dell'array */
  append: (item: T) => T;
  /** Aggiorna un elemento */
  update: (prev: T, item: T) => void;
  /** Aggiorna un elemento dato l'indice */
  updateAt: (index: number, item: T) => void;
  /** Rimuove un elemento */
  remove: (item: T | null | undefined) => void;
  /** Rimuove un elemento dato l'indice */
  removeAt: (index: number) => void;
  /** Inverte di posizione di due elementi */
  swap: (a: number, b: number) => void;
  /** Muove la posizione di due elementi spostando anche gli intermedi */
  move: (a: number, b: number) => void;
  /** Sostituisce tutti gli elementi */
  replace: (items: T[]) => void;
  /** Ritorna i valori attuali dell'array */
  getValue: () => T[];
}

/**
 * Editor per gestire la modifica di un array dentro React Hook Form. Include un
 * `handle` per gestire la modal di modifica dell'elemento attivo.
 */
export interface RelationEditor<T> extends BaseRelationEditor<T> {
  /** Handle di modifica dell'elemento corrente (per Modal o Drawer) */
  handle: RelationActiveHandle<T>;
}

/**
 * Aggiunge un campo `id` a un oggetto se non è già presente. Questa tipizzazione
 * è necessaria per poter descrivere via TypeScript il comportamento di
 * `useFieldArray` di React Hook Form, che aggiunge il campo ID come UUID
 * automaticamente.
 */
export type WithId<T> = T extends { __uid?: any } ? T : T & { __uid?: string };

/**
 * Hook per gestire la modifica di un array dentro React Hook Form.
 */
export function useRelationEditor<T>(name: string): RelationEditor<T> {
  const methods = useFormContext();
  const { getValues } = methods;

  /**
   * È fondamentale registrare il campo prima di usare `useFieldArray` in
   * react-hook-form. Se non lo facciamo, il campo non viene riconosciuto
   * e i suoi valori non vengono aggiornati fino al primo "append", ecc.
   */
  methods.register(name);

  const {
    fields,
    append,
    // prepend,
    update: baseUpdate,
    remove: baseRemove,
    swap,
    move,
    insert: baseInsert,
    replace,
  } = useFieldArray({ control: methods.control, name, keyName: '__uid' });

  const getValue = useCallback(() => getValues(name), [getValues, name]);

  const items: Array<WithId<T>> = useMemo(
    () => (fields as any) ?? [],
    [fields],
  );

  /** Aggiorna un elemento */
  const update = useCallback(
    (prev: WithId<T>, item: WithId<T>) => {
      const index = items.indexOf(prev);
      if (index === -1) {
        console.error(
          `Impossibile trovare l'elemento da aggiornare. Elemento non trovato.`,
          { prev, item },
        );
        return;
      }

      // Evitiamo di perdere le proprietà del vecchio oggetto.
      const updated = Object.assign(cloneDeep(prev as object), item);

      baseUpdate(index, updated);
    },
    [items, baseUpdate],
  );

  /** Rimuove un elemento */
  const remove = useCallback(
    (item: WithId<T> | null | undefined) => {
      if (item == null) return;
      // Ricaviamo l'indice dell'elemento da rimuovere
      const index = items.indexOf(item);
      baseRemove(index);
    },
    [items, baseRemove],
  );

  const insert: BaseRelationEditor<T>['insert'] = useCallback(
    (index: number, item: WithId<T>) => {
      baseInsert(index, item, { shouldFocus: true });
      return item;
    },
    [baseInsert],
  );

  const editor = {
    items,
    insert,
    append: append as BaseRelationEditor<T>['append'],
    update,
    updateAt: baseUpdate as BaseRelationEditor<T>['updateAt'],
    swap,
    move,
    remove,
    replace,
    removeAt: baseRemove as BaseRelationEditor<T>['removeAt'],
    getValue,
  };

  const handle = useRelationActiveHandle<T>(editor);
  return {
    ...editor,
    handle,
  };
}
